// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3H: simple-popup edit hydration.
//
// The 5 simple-create popups (Glass / Hardware / Profile / Jobwork /
// Ecommerce) double as the edit surface now that the Product Creation
// Studio is no longer reachable from Item Master. This helper:
//
//   1. Probes every extension endpoint in parallel — the first one
//      that responds 200 picks the popup `kind`. Falls back to a
//      best-guess on item flags when no extension exists yet.
//   2. Flattens core + extension into the friendly field names the
//      popup forms bind to (`glassCategoryLabel`, `profileMaterialLabel`,
//      `serviceTypeLabel`, `chargeBasisLabel`, …) — the reverse of the
//      splits done in simpleCreateHelpers.
// ─────────────────────────────────────────────────────────────────

import { itemsApi as defaultItemsApi } from './itemsApi';
import { SIMPLE_KINDS, GLASS_CATEGORY_OPTIONS, PROFILE_MATERIAL_OPTIONS,
  JOBWORK_SERVICE_OPTIONS, JOBWORK_CHARGE_BASIS_OPTIONS } from './simpleCreateConstants';

async function safeGet(fn) {
  try {
    const res = await fn();
    // Treat empty / null / array responses as "no extension row" so the
    // kind inference doesn't get tricked by an endpoint that returns
    // 200 with a stub object instead of 404.
    if (!res || typeof res !== 'object' || Array.isArray(res)) return null;
    if (Object.keys(res).length === 0) return null;
    return res;
  } catch (_e) { return null; }
}

function inferKindFromFlags(core) {
  if (!core) return SIMPLE_KINDS.HARDWARE;
  if (core.itemType === 'glass')   return SIMPLE_KINDS.GLASS;
  if (core.itemType === 'service' || core.isService === true || core.isJobworkItem === true)
    return SIMPLE_KINDS.JOBWORK;
  return SIMPLE_KINDS.HARDWARE;
}

// itemType is the most reliable signal when it is a specific kind
// ('glass', 'service'). For ambiguous 'finished_good' / 'trading_good'
// rows we fall back to which extension actually exists.
function pickKind(core, { glass, profile, hardware, jobwork, ecom }) {
  if (core && core.itemType === 'glass')   return SIMPLE_KINDS.GLASS;
  if (core && (core.itemType === 'service' || core.isService === true || core.isJobworkItem === true))
    return SIMPLE_KINDS.JOBWORK;
  if (ecom)     return SIMPLE_KINDS.ECOMMERCE;
  if (jobwork)  return SIMPLE_KINDS.JOBWORK;
  if (glass)    return SIMPLE_KINDS.GLASS;
  if (profile)  return SIMPLE_KINDS.PROFILE;
  if (hardware) return SIMPLE_KINDS.HARDWARE;
  return inferKindFromFlags(core);
}

function hydrateGlass(core, ext) {
  const out = { ...core, ...(ext || {}) };
  if (ext && ext.glassFamily) {
    const match = GLASS_CATEGORY_OPTIONS.find((o) =>
      o.glassFamily === ext.glassFamily &&
      (ext.glassColor == null || o.glassColor === ext.glassColor)
    );
    if (match) {
      out.glassCategoryLabel = match.label;
    } else if (ext.glassColor) {
      // Stage 6B.8A-3N: DB-only category (added via Glass Category
      // Master after the hardcoded seeds shipped) — the preserved
      // glass_color IS the label that was picked at create time, so
      // re-use it directly. Keeps edit mode showing the same row.
      out.glassCategoryLabel = ext.glassColor;
    }
  }
  // Brand was stuffed into spec.remarks as "Brand: X. <rest>" on create.
  if (ext && typeof ext.remarks === 'string') {
    const m = ext.remarks.match(/^Brand:\s*([^.]+)\.?\s*(.*)$/);
    if (m) {
      out.brand   = m[1].trim();
      out.remarks = m[2] ? m[2].trim() : '';
    }
  }
  return out;
}

function hydrateProfile(core, ext) {
  const out = { ...core, ...(ext || {}) };
  if (ext && ext.profileMaterial) {
    const match = PROFILE_MATERIAL_OPTIONS.find((o) => o.value === ext.profileMaterial);
    if (match) out.profileMaterialLabel = match.label;
  }
  return out;
}

function hydrateJobwork(core, ext) {
  const out = { ...core, ...(ext || {}) };
  if (ext && ext.jobworkType) {
    const match = JOBWORK_SERVICE_OPTIONS.find((o) =>
      o.jobworkType === ext.jobworkType &&
      (ext.jobworkCategory == null || o.jobworkCategory === ext.jobworkCategory)
    );
    if (match) out.serviceTypeLabel = match.label;
  }
  if (ext && ext.chargeBasis) {
    const match = JOBWORK_CHARGE_BASIS_OPTIONS.find((o) => o.value === ext.chargeBasis);
    if (match) out.chargeBasisLabel = match.label;
  }
  return out;
}

function hydrateHardware(core, ext) {
  return { ...core, ...(ext || {}) };
}

function hydrateEcommerce(core, ext) {
  return { ...core, ...(ext || {}) };
}

export async function loadItemForEdit(itemId, deps = {}) {
  const api = deps.itemsApi || defaultItemsApi;
  const core = await api.get(itemId);

  // Probe extensions + set definition in parallel. First present
  // extension wins kind selection (ecommerce → jobwork → glass →
  // profile → hardware). The set payload is independent — Hardware
  // popup uses it to populate the "Create as Hardware Set" surface
  // when an existing item already has components attached.
  const [glass, profile, hardware, jobwork, ecom, set] = await Promise.all([
    safeGet(() => api.getGlassSpec(itemId)),
    safeGet(() => api.getProfileSpec(itemId)),
    safeGet(() => api.getHardwareSpec(itemId)),
    safeGet(() => api.getJobworkSpec(itemId)),
    safeGet(() => api.getEcommerceProfile(itemId)),
    safeGet(() => (typeof api.getItemSet === 'function'
      ? api.getItemSet(itemId)
      : Promise.resolve(null)))
  ]);

  const kind = pickKind(core, { glass, profile, hardware, jobwork, ecom });
  let initialValues;
  if (kind === SIMPLE_KINDS.ECOMMERCE) initialValues = hydrateEcommerce(core, ecom);
  else if (kind === SIMPLE_KINDS.JOBWORK) initialValues = hydrateJobwork(core, jobwork);
  else if (kind === SIMPLE_KINDS.GLASS)   initialValues = hydrateGlass(core, glass);
  else if (kind === SIMPLE_KINDS.PROFILE) initialValues = hydrateProfile(core, profile);
  else                                    initialValues = hydrateHardware(core, hardware);

  // Surface the existing set + its components on the initialValues
  // so the popup can switch the "Create as Hardware Set" toggle ON
  // and pre-fill the component grid.
  if (set && set.set) {
    initialValues = {
      ...initialValues,
      _isSet:                    true,
      _setType:                  set.set.setType || 'hardware_set',
      _setName:                  set.set.setName || '',
      _isStockDeductAsComponents: set.set.isStockDeductAsComponents === true,
      _isPriceFromComponents:    set.set.isPriceFromComponents === true,
      _setRemarks:               set.set.remarks || '',
      _setComponents:            Array.isArray(set.components) ? set.components : []
    };
  }

  return { kind, editRecord: core, initialValues };
}
